import React, { useMemo } from 'react';
import {
  Card,
  makeStyles,
  tokens,
  Caption1,
  Skeleton,
  SkeletonItem
} from '@fluentui/react-components';
import {
  ErrorCircle24Regular,
  CalendarClock24Regular,
  Flash24Regular,
  Checkmark16Regular,
  Dismiss16Regular,
  Send24Regular,
  ArrowClockwise16Regular
} from '@fluentui/react-icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import type { QueueCharts, ChartDataPoint, OperationsChartDataPoint } from '../../types';
import { dashboardService } from '../../services/dashboardService';

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
    '@media (max-width: 1400px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  totalCard: {
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
  },
  totalIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    flexShrink: 0,
  },
  iconError: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  iconScheduled: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  iconProcessing: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  iconActive: {
    backgroundColor: '#e0f2fe',
    color: '#3090e5',
  },
  totalValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    lineHeight: 1.2,
  },
  totalValueError: {
    color: tokens.colorPaletteRedForeground1,
  },
  totalValueScheduled: {
    color: tokens.colorPaletteGreenForeground1,
  },
  totalValueProcessing: {
    color: tokens.colorPaletteYellowForeground2,
  },
  totalValueActive: {
    color: '#3090e5',
  },
  rateContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  rateValue: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#3090e5',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  ratesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginLeft: 'auto',
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums',
  },
  incomingRate: {
    color: '#f59e0b',  // Laranja - entrantes
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  outgoingRate: {
    color: '#3090e5',  // Azul - saintes (mesma cor da fila)
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  rateNumber: {
    minWidth: '45px',
    textAlign: 'right' as const,
  },
  ratePerSecond: {
    minWidth: '55px',
  },
  processingDetails: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '16px',
    marginLeft: 'auto',
  },
  processingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
  },
  successText: {
    color: tokens.colorPaletteGreenForeground1,
  },
  failureText: {
    color: tokens.colorPaletteRedForeground1,
  },
  scheduledText: {
    color: '#f59e0b',  // Laranja - mensagens novas para scheduled
  },
  chartCard: {
    padding: '18px',
    backgroundColor: '#4a4a4a',
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '14px',
  },
  chartTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#ffffff',
  },
  chartSubtitle: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  chartContainer: {
    height: '248px',
  },
  noData: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '248px',
    color: 'rgba(255, 255, 255, 0.5)',
  },
  skeletonSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
});

// Tipo para pontos do gráfico de mensagens ativas (tempo real + agregado por hora)
interface ActiveChartDataPoint {
  time: string;
  count: number;
  isHourlyAggregate?: boolean;
}

// Tipo interno com valor numérico para posicionamento no eixo X
interface ActiveChartDataPointNumeric {
  time: string;
  timeValue: number; // minutos desde meia-noite (pode ser > 1440 para próximo dia)
  count: number | null;
  isHourlyAggregate?: boolean;
  isFuturePadding?: boolean;
}

// Converte "HH:mm" ou "HH:mm:ss" para minutos desde meia-noite
const timeToMinutes = (time: string): number => {
  const parts = time.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  return hours * 60 + minutes;
};

// Formata minutos para "HH:mm" (usado nos ticks do eixo X)
const minutesToTime = (mins: number): string => {
  const normalizedMins = mins % 1440; // Normaliza para 24h
  const h = Math.floor(normalizedMins / 60);
  const m = normalizedMins % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

// Tipo para pontos do gráfico de DLQ e Scheduled
interface QueueCountsChartDataPoint {
  time: string;
  count: number;
  isHourlyAggregate?: boolean;
}

interface QueueChartsSectionProps {
  queueCharts: QueueCharts | null;
  loading?: boolean;
  activeHistory?: ActiveChartDataPoint[];
  currentActiveCount?: number;
  incomingPerSecond?: number | null;
  incomingPerMinute?: number | null;
  outgoingPerSecond?: number | null;
  outgoingPerMinute?: number | null;
  realtimeDlqCount?: number | null;
  realtimeScheduledCount?: number | null;
  dlqHistory?: QueueCountsChartDataPoint[];
  scheduledHistory?: QueueCountsChartDataPoint[];
}

// Tooltip customizado para o gráfico de Mensagens Ativas (diferencia agregado vs tempo real)
const ActiveMessagesTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const point = payload[0].payload;
    const isAggregate = point?.isHourlyAggregate;
    const timeLabel = point?.time || ''; // Usa o horário formatado do ponto

    return (
      <div style={{
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        padding: '8px 12px',
        borderRadius: '4px',
        boxShadow: tokens.shadow4,
      }}>
        <p style={{ margin: 0, fontWeight: 600 }}>
          {isAggregate ? `Hora: ${timeLabel}` : timeLabel}
        </p>
        <p style={{ margin: 0, color: payload[0].color }}>
          {isAggregate ? 'Pico: ' : ''}{payload[0].value?.toLocaleString('pt-BR')} mensagens
        </p>
        {isAggregate && (
          <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#888' }}>
            (valor máximo da hora)
          </p>
        )}
      </div>
    );
  }
  return null;
};

// Tooltip customizado para o gráfico de Processamento (sucesso/falha/novas para retry)
const OperationsTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        padding: '8px 12px',
        borderRadius: '4px',
        boxShadow: tokens.shadow4,
      }}>
        <p style={{ margin: 0, fontWeight: 600 }}>{label}</p>
        <p style={{ margin: '4px 0 0 0', color: '#22c55e' }}>
          Sucesso: {payload.find((p: any) => p.dataKey === 'success')?.value ?? 0}
        </p>
        <p style={{ margin: '2px 0 0 0', color: '#ef4444' }}>
          Falhas: {payload.find((p: any) => p.dataKey === 'failure')?.value ?? 0}
        </p>
        <p style={{ margin: '2px 0 0 0', color: '#f59e0b' }}>
          Novas p/ Retry: {payload.find((p: any) => p.dataKey === 'newToScheduled')?.value ?? 0}
        </p>
      </div>
    );
  }
  return null;
};

export const QueueChartsSection: React.FC<QueueChartsSectionProps> = ({
  queueCharts,
  loading,
  activeHistory = [],
  currentActiveCount = 0,
  incomingPerSecond = null,
  incomingPerMinute = null,
  outgoingPerSecond = null,
  outgoingPerMinute = null,
  realtimeDlqCount = null,
  realtimeScheduledCount = null,
  dlqHistory = [],
  scheduledHistory = []
}) => {
  const styles = useStyles();

  if (loading) {
    return (
      <div className={styles.container}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.skeletonSection}>
            <Card className={styles.totalCard}>
              <Skeleton>
                <SkeletonItem shape="circle" size={48} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <SkeletonItem size={28} style={{ width: '60px' }} />
                  <SkeletonItem size={16} style={{ width: '120px' }} />
                </div>
              </Skeleton>
            </Card>
            <Card className={styles.chartCard}>
              <Skeleton>
                <SkeletonItem size={128} style={{ width: '100%' }} />
              </Skeleton>
            </Card>
          </div>
        ))}
      </div>
    );
  }

  // Preparar dados para os gráficos
  const prepareChartData = (history: ChartDataPoint[] | undefined) => {
    if (!history || history.length === 0) return [];

    return history.map(point => ({
      hour: point.hourLabel,
      count: point.count,
    }));
  };

  // Preparar dados para o gráfico de operações (sucesso/falha/novas para scheduled)
  const prepareOperationsData = (history: OperationsChartDataPoint[] | undefined) => {
    if (!history || history.length === 0) return [];

    return history.map(point => ({
      hour: point.hourLabel,
      success: point.successCount,
      failure: point.failureCount,
      newToScheduled: point.newToScheduledCount ?? 0,
    }));
  };

  const dlqData = prepareChartData(queueCharts?.dlqHistory);
  const scheduledData = prepareChartData(queueCharts?.scheduledHistory);
  const operationsData = prepareOperationsData(queueCharts?.operationsHistory);

  // Calcular percentuais (incluindo novas para scheduled)
  const totalNewToScheduled = queueCharts?.totalNewToScheduled ?? 0;
  const totalOperations = (queueCharts?.totalSuccess ?? 0) + (queueCharts?.totalFailure ?? 0) + totalNewToScheduled;
  const successPercent = totalOperations > 0
    ? Math.round((queueCharts?.totalSuccess ?? 0) / totalOperations * 100)
    : 0;
  const failurePercent = totalOperations > 0
    ? Math.round((queueCharts?.totalFailure ?? 0) / totalOperations * 100)
    : 0;
  const newToScheduledPercent = totalOperations > 0
    ? Math.round(totalNewToScheduled / totalOperations * 100)
    : 0;

  // Calcula domínio Y fixo: sempre começa em 0 e vai até o máximo histórico
  const allPointsKey = activeHistory
    .map(p => `${p.time}:${p.count}`)
    .join('|');

  const yDomain = useMemo((): [number, number] => {
    if (activeHistory.length === 0) return [0, 100000];

    const values = activeHistory.map(p => p.count);
    const max = Math.max(...values);
    const padding = Math.max(max * 0.1, 5000);

    return [
      0,
      Math.ceil((max + padding) / 1000) * 1000
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPointsKey]);

  // Horário atual do ponto não agregado (com segundos)
  const currentPoint = activeHistory.find(p => !p.isHourlyAggregate);
  const currentPointTime = currentPoint?.time || '';

  // Converte dados para formato numérico e adiciona ponto futuro
  const { chartData, xDomain, xTicks } = useMemo(() => {
    if (activeHistory.length === 0) {
      return { chartData: [], xDomain: [0, 1440] as [number, number], xTicks: [] };
    }

    // Encontra o primeiro ponto agregado para determinar o início das 24h
    const firstHourlyPoint = activeHistory.find(p => p.isHourlyAggregate);
    const baseMinutes = firstHourlyPoint ? timeToMinutes(firstHourlyPoint.time) : 0;

    // Separa pontos agregados e ponto atual
    const aggregatedPoints = activeHistory.filter(p => p.isHourlyAggregate);
    const currentPoint = activeHistory.find(p => !p.isHourlyAggregate);

    // Converte pontos agregados para formato numérico
    // Usa a ordem da array (que já vem cronológica do backend) para garantir ordem crescente
    const numericAggregated: ActiveChartDataPointNumeric[] = [];
    let prevTimeValue = -1;

    aggregatedPoints.forEach(p => {
      let timeValue = timeToMinutes(p.time);
      // Se o timeValue atual for menor ou igual ao anterior, cruzou meia-noite
      // Adiciona 1440 para manter a ordem crescente
      if (prevTimeValue >= 0 && timeValue <= prevTimeValue) {
        timeValue += 1440;
      }
      prevTimeValue = timeValue;

      numericAggregated.push({
        time: p.time,
        timeValue,
        count: p.count,
        isHourlyAggregate: true,
      });
    });

    // Encontra o maior timeValue dos pontos agregados
    const maxAggregatedTimeValue = numericAggregated.length > 0
      ? Math.max(...numericAggregated.map(p => p.timeValue))
      : baseMinutes;

    // Processa o ponto atual (sempre deve ficar no final)
    const numericData: ActiveChartDataPointNumeric[] = [...numericAggregated];
    let currentTimeValue = maxAggregatedTimeValue;

    if (currentPoint) {
      currentTimeValue = timeToMinutes(currentPoint.time);
      // Se o timeValue do ponto atual for menor ou igual ao máximo agregado,
      // precisa adicionar 1440 para posicioná-lo corretamente no final
      if (currentTimeValue <= maxAggregatedTimeValue) {
        currentTimeValue += 1440;
      }

      // Verifica se há um gap significativo (mais de 150 minutos) entre o último agregado e o ponto atual
      // Se houver, insere um ponto null para quebrar a linha (evita linha cortando o gráfico)
      const gapMinutes = currentTimeValue - maxAggregatedTimeValue;
      if (gapMinutes > 150) {
        numericData.push({
          time: '',
          timeValue: maxAggregatedTimeValue + 1, // Logo após o último agregado
          count: null, // null para quebrar a linha
          isHourlyAggregate: false,
          isFuturePadding: true, // Marca como padding para não aparecer no tooltip
        });
      }

      numericData.push({
        time: currentPoint.time,
        timeValue: currentTimeValue,
        count: currentPoint.count,
        isHourlyAggregate: false,
      });
    }

    // Ponto atual (não agregado, não padding) para cálculos posteriores
    const currentNumericPoint = numericData.find(p => !p.isHourlyAggregate && !p.isFuturePadding);

    // Adiciona ponto futuro (30 min à frente) para criar espaço
    const futureTimeValue = currentTimeValue + 30;
    numericData.push({
      time: minutesToTime(futureTimeValue),
      timeValue: futureTimeValue,
      count: null,
      isFuturePadding: true,
    });

    // Domínio X: do primeiro ponto até 30 min após o horário atual
    const minX = Math.min(...numericData.filter(p => !p.isFuturePadding).map(p => p.timeValue));
    const maxX = futureTimeValue;

    // Gera ticks de 3 em 3 horas começando do primeiro ponto + horário atual
    const ticks: number[] = [];
    const hourlyPoints = numericData.filter(p => p.isHourlyAggregate);
    if (hourlyPoints.length > 0) {
      // Começa do primeiro ponto e seleciona de 3 em 3
      for (let i = 0; i < hourlyPoints.length; i += 3) {
        ticks.push(hourlyPoints[i].timeValue);
      }
    }
    // Adiciona o horário atual aos ticks, mas só se não estiver muito próximo do último tick
    if (currentNumericPoint) {
      const lastTick = ticks.length > 0 ? ticks[ticks.length - 1] : 0;
      // Só adiciona se estiver a mais de 90 minutos do último tick agregado
      if (currentNumericPoint.timeValue - lastTick > 90) {
        ticks.push(currentNumericPoint.timeValue);
      }
    }

    return {
      chartData: numericData,
      xDomain: [minX, maxX] as [number, number],
      xTicks: ticks.sort((a, b) => a - b),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPointsKey, currentPointTime]);

  return (
    <div className={styles.container}>
      {/* Seção de Mensagens Ativas (tempo real) */}
      <div className={styles.section}>
        <Card className={styles.totalCard}>
          <div className={`${styles.totalIcon} ${styles.iconActive}`}>
            <Send24Regular />
          </div>
          <div className={styles.rateContainer}>
            <div className={`${styles.totalValue} ${styles.totalValueActive}`}>
              {dashboardService.formatNumber(currentActiveCount)}
            </div>
            {(incomingPerMinute !== null || outgoingPerMinute !== null) && (
              <div className={styles.ratesContainer}>
                {incomingPerMinute !== null && (
                  <span className={styles.incomingRate}>
                    &#8593;
                    <span className={styles.rateNumber}>{incomingPerMinute.toString().padStart(4, '\u00A0')}</span>
                    <span>msg/min</span>
                    {incomingPerSecond !== null && (
                      <span className={styles.ratePerSecond}>({incomingPerSecond.toFixed(1).padStart(4, '\u00A0')}/s)</span>
                    )}
                  </span>
                )}
                {outgoingPerMinute !== null && (
                  <span className={styles.outgoingRate}>
                    &#8595;
                    <span className={styles.rateNumber}>{outgoingPerMinute.toString().padStart(4, '\u00A0')}</span>
                    <span>msg/min</span>
                    {outgoingPerSecond !== null && (
                      <span className={styles.ratePerSecond}>({outgoingPerSecond.toFixed(1).padStart(4, '\u00A0')}/s)</span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        </Card>

        <Card className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Mensagens Ativas</span>
            <span className={styles.chartSubtitle}>Últimas 24 horas</span>
          </div>
          <div className={styles.chartContainer}>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis
                    dataKey="timeValue"
                    type="number"
                    domain={xDomain}
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    ticks={xTicks}
                    tickFormatter={(value) => {
                      // Encontra o ponto correspondente para mostrar o horário correto
                      const point = chartData.find(p => p.timeValue === value);
                      if (point && !point.isHourlyAggregate && !point.isFuturePadding) {
                        // Ponto atual: mostra com segundos
                        return point.time;
                      }
                      // Pontos agregados: mostra HH:mm
                      return minutesToTime(value);
                    }}
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    allowDecimals={false}
                    domain={yDomain}
                  />
                  <Tooltip content={<ActiveMessagesTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#3090e5"
                    strokeWidth={2}
                    isAnimationActive={false}
                    connectNulls={false}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      // Ignora ponto futuro (padding)
                      if (payload?.isFuturePadding || payload?.count === null) {
                        return null;
                      }
                      // Mostrar dot destacado no ponto atual (tempo real)
                      if (!payload?.isHourlyAggregate) {
                        return <circle cx={cx} cy={cy} r={5} fill="#3090e5" stroke="#fff" strokeWidth={2} />;
                      }
                      // Mostrar dots em todos os pontos agregados (histórico 24h)
                      if (payload?.isHourlyAggregate) {
                        return <circle cx={cx} cy={cy} r={3} fill="#3090e5" stroke="none" />;
                      }
                      return null;
                    }}
                    activeDot={{ r: 5, fill: '#3090e5' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <Caption1>Aguardando dados...</Caption1>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Seção de Processamento (Sucesso/Falha) */}
      <div className={styles.section}>
        <Card className={styles.totalCard}>
          <div className={`${styles.totalIcon} ${styles.iconProcessing}`}>
            <Flash24Regular />
          </div>
          <div className={`${styles.totalValue} ${styles.totalValueProcessing}`}>
            {dashboardService.formatNumber(totalOperations)}
          </div>
          <div className={styles.processingDetails}>
            <div className={styles.processingRow}>
              <Checkmark16Regular className={styles.successText} />
              <span className={styles.successText}>
                {dashboardService.formatNumber(queueCharts?.totalSuccess ?? 0)} ({successPercent}%)
              </span>
            </div>
            <div className={styles.processingRow}>
              <Dismiss16Regular className={styles.failureText} />
              <span className={styles.failureText}>
                {dashboardService.formatNumber(queueCharts?.totalFailure ?? 0)} ({failurePercent}%)
              </span>
            </div>
            <div className={styles.processingRow}>
              <ArrowClockwise16Regular className={styles.scheduledText} />
              <span className={styles.scheduledText}>
                {dashboardService.formatNumber(totalNewToScheduled)} ({newToScheduledPercent}%)
              </span>
            </div>
          </div>
        </Card>

        <Card className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Processamento</span>
            <span className={styles.chartSubtitle}>Últimas 24 horas</span>
          </div>
          <div className={styles.chartContainer}>
            {operationsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={operationsData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<OperationsTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="success"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Sucesso"
                  />
                  <Line
                    type="monotone"
                    dataKey="failure"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={{ fill: '#ef4444', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Falhas"
                  />
                  <Line
                    type="monotone"
                    dataKey="newToScheduled"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ fill: '#f59e0b', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Novas p/ Retry"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <Caption1>Nenhum dado disponível</Caption1>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Seção de Erros (DLQ) */}
      <div className={styles.section}>
        <Card className={styles.totalCard}>
          <div className={`${styles.totalIcon} ${styles.iconError}`}>
            <ErrorCircle24Regular />
          </div>
          <div className={`${styles.totalValue} ${styles.totalValueError}`}>
            {dashboardService.formatNumber(realtimeDlqCount ?? queueCharts?.totalErrors ?? 0)}
          </div>
        </Card>

        <Card className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Dead Letter Queue</span>
            <span className={styles.chartSubtitle}>Últimas 24 horas</span>
          </div>
          <div className={styles.chartContainer}>
            {(dlqHistory.length > 0 || dlqData.length > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={dlqHistory.length > 0 ? dlqHistory : dlqData}
                  margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<ActiveMessagesTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#ef4444"
                    strokeWidth={2}
                    isAnimationActive={false}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      if (!payload?.isHourlyAggregate) {
                        return <circle cx={cx} cy={cy} r={5} fill="#ef4444" stroke="#fff" strokeWidth={2} />;
                      }
                      if (payload?.isHourlyAggregate) {
                        return <circle cx={cx} cy={cy} r={3} fill="#ef4444" stroke="none" />;
                      }
                      return null;
                    }}
                    activeDot={{ r: 5, fill: '#ef4444' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <Caption1>Nenhum dado disponível</Caption1>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Seção de Mensagens Agendadas */}
      <div className={styles.section}>
        <Card className={styles.totalCard}>
          <div className={`${styles.totalIcon} ${styles.iconScheduled}`}>
            <CalendarClock24Regular />
          </div>
          <div className={`${styles.totalValue} ${styles.totalValueScheduled}`}>
            {dashboardService.formatNumber(realtimeScheduledCount ?? queueCharts?.totalScheduled ?? 0)}
          </div>
        </Card>

        <Card className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Reagendamentos</span>
            <span className={styles.chartSubtitle}>Últimas 24 horas</span>
          </div>
          <div className={styles.chartContainer}>
            {(scheduledHistory.length > 0 || scheduledData.length > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={scheduledHistory.length > 0 ? scheduledHistory : scheduledData}
                  margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<ActiveMessagesTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#22c55e"
                    strokeWidth={2}
                    isAnimationActive={false}
                    dot={(props: any) => {
                      const { cx, cy, payload } = props;
                      if (!payload?.isHourlyAggregate) {
                        return <circle cx={cx} cy={cy} r={5} fill="#22c55e" stroke="#fff" strokeWidth={2} />;
                      }
                      if (payload?.isHourlyAggregate) {
                        return <circle cx={cx} cy={cy} r={3} fill="#22c55e" stroke="none" />;
                      }
                      return null;
                    }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <Caption1>Nenhum dado disponível</Caption1>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default QueueChartsSection;
